import { useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotFoundResult from '../../../components/results/NotFoundResult';
import TokenExpirationController from '../../../components/TokenExpirationController';
import LoginLayout from '../../../layouts/LoginLayout';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import ChangePassword, { TranslationType } from '../ResetPasswordPage/ChangePassword';
import SmsOtp from '../ResetPasswordPage/SmsOtp';
import TokenExpired from '../SignInPage/TokenExpired';

type State =
    | { step: 'setupPassword'; token: string }
    | { step: 'smsOtp'; token: string }
    | { step: 'invalidToken'; token: string };

type Action =
    | { type: 'moveToSmsOTP'; token: string }
    | { type: 'moveToAfterSmsOTPSuccess'; token: string }
    | { type: 'moveToNotFoundResult'; token: string };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'moveToSmsOTP':
            return { step: 'smsOtp', token: action.token };

        case 'moveToAfterSmsOTPSuccess':
            return { step: 'setupPassword', token: action.token };

        case 'moveToNotFoundResult':
            return { step: 'invalidToken', token: action.token };

        default:
            return state;
    }
};

export type ActionHandlers = {
    moveToSmsOTP: (token: string) => void;
    moveToAfterSmsOTPSuccess: (token: string) => void;
};

const ActivateUserAccountPage = () => {
    const locationState = useLocation().state as { token?: string; verified?: boolean };
    const navigate = useNavigate();

    const initialState: State = useMemo(() => {
        if (!locationState?.token) {
            return { step: 'invalidToken', token: locationState.token };
        }

        if (locationState?.verified) {
            return { step: 'setupPassword', token: locationState.token };
        }

        return { step: 'smsOtp', token: locationState.token };
    }, [locationState]);

    const [state, dispatch] = useReducer(reducer, initialState);

    const actions = useMemo(
        (): ActionHandlers => ({
            moveToSmsOTP: (token: string) => dispatch({ type: 'moveToSmsOTP', token }),
            moveToAfterSmsOTPSuccess: (token: string) => dispatch({ type: 'moveToAfterSmsOTPSuccess', token }),
        }),
        [dispatch]
    );

    const stepElement = (() => {
        const tokenExpiredFallback = (
            <LoginContent>
                <TokenExpired onLinkClick={() => navigate('../signIn', { replace: true })} />
            </LoginContent>
        );

        switch (state.step) {
            case 'invalidToken':
                return <NotFoundResult />;

            case 'smsOtp':
                return (
                    <TokenExpirationController fallback={tokenExpiredFallback} token={state.token}>
                        <SmsOtp actions={actions} token={state.token} />
                    </TokenExpirationController>
                );

            case 'setupPassword':
                return (
                    <TokenExpirationController fallback={tokenExpiredFallback} token={state.token}>
                        <ChangePassword token={state.token} translationType={TranslationType.SetupPassword} />
                    </TokenExpirationController>
                );

            default:
                return null;
        }
    })();

    return <LoginLayout>{stepElement}</LoginLayout>;
};

export default ActivateUserAccountPage;
